import React, { useRef, useState, useMemo, useEffect, forwardRef, useImperativeHandle } from "react";
import { useGLTF, ContactShadows, useAnimations } from "@react-three/drei";
import { advance, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import AudioLooper from "../../components/AudioLooper";
import { EXTERIOR_RESOURCES } from '../../assetsDictionary';
import useHelicopterMovement from '../../hooks/useHelicopterMovement';
import { EXT_STATE } from '../../states';

// TAKEOFF (INTRO)
const INTRO_CLIPS = [
  "Helix_spinUp",
  "Helix_spinUp_inverse_2",
  "Helix_spinUp_inverse_3",
  "Helix_spinUp_inverse_5",
  "Helix_spinUp_4",
  "Helix_spinUp_6",
  "Motor_frontLeft_rotation",
  "Motor_front_right",
  "Motor_leftback_rotation",
  "Motor_leftMiddle_rotation",
  "Motor_rightBack_rotation",
  "Motor_rightMiddle_rotation",
  "Structure_03Action",
];

// FLYING LOOP
const LOOP_CLIPS = [
  "Helix_spin",
  "Helix_spin_2",
  "Helix_spin_3",
  "Helix_spin_4",
  "Helix_spin_5",
  "Helix_spin_6",
  "Motor_RightMiddle_horizontal",
  "Motor_frontleft_horizontal",
  "Motor_frontright_horizontal",
  "Motor_LeftBack_horizontal",
  "motor_leftMiddle_horizontal",
  "Motor_RightBack_horizontal",
];

// LANDING (END)
const END_CLIPS = [
  "Helix_Landing",
  "Helix_landing_inverse_2",
  "Helix_landing_inverse_3",
  "Helix_landing_inverse_5",
  "Helix_landing_4",
  "Helix_landing_6",
  "Motor_frontLeft_rotation_inverse",
  "Motor_frontRight_inverse_rotation",
  "Motor_leftBack_inverse_rotation",
  "motor_leftMiddle_inverse_rotation",
  "Motor_rightBack_inverse_rotation",
  "Motor_rightMiddle_inverse_rotation",
  "Structure_03Action.002",
];

const TRANSFORMS = {
  start: {
    position: new THREE.Vector3(-5, 4,-3),
    rotationY:  Math.PI + Math.PI / 2, // mirando a la derecha
  },

  loop: {
    position: new THREE.Vector3(0, 0.5, 0),
    rotationY: Math.PI, // mirando al frente
  },
  land: {
    position: new THREE.Vector3(0, -0.5, 0),
    rotationY: Math.PI, // gira para aterrizar
  },
  flyAway: {
    position: new THREE.Vector3(3, 3.5, 6),
    rotationY: Math.PI, // mirando a la izquierda
  },
};



const Helicopter = forwardRef(function Helicopter( props, ref) {
      useEffect(() => {
        console.log('[LOG] Helicopter MOUNT');
        return () => {
          console.log('[LOG] Helicopter UNMOUNT');
        };
      }, []);

  // Ref to track if auto start logic has run
  const didAutoStartRef = useRef(false);

  const { 
    playing = true, 
    onReady,
    flightState, 
    // scale,
    // rotation-y,
    castShadow, 
    lightPosition: lightPos,
    advanceFlightState,
    ...primitiveProps // se usan?
   } = props;

  // 2. Declarar dragStateRef antes del hook
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    lastY: 0, // última rotación Y del rig
    initialized: false
  });


  const helicopterRef = useRef();
  const targetShadowOpacityRef = useRef(0);
  const readyRef = useRef(false);
  const introStartedRef = useRef(false);
  const [shadowOpacity, setShadowOpacity] = useState(0);
  const { scene: originalScene, animations } = useGLTF(EXTERIOR_RESOURCES.Helicoptero3D.url);
  const { actions } = useAnimations(animations, helicopterRef);
  const readySentRef = useRef(false);
  const rigRef = useRef();
  const { moveRef, moveTo, updateMovement } = useHelicopterMovement(rigRef);
  // Ref auxiliar para exponer el valor actualizado de moveRef.current.active a los handlers
  const moveActiveRef = useRef(false);
  // Sincronizar moveActiveRef en cada frame
  useFrame(() => {
    moveActiveRef.current = moveRef.current.active;
  });
  const afterTakeoffTimeoutRef = useRef(null);
  const lastIntroWasTakeoffRef = useRef(false);
  const flyBackTimeoutRef = useRef(null);
  const suppressIntroAutoFlyRef = useRef(false);



  // ROTACION MANUAL POR DRAG
  useEffect(() => {

    // Inicializar la rotación base del rig al arrancar
    if (rigRef.current && !dragStateRef.current.initialized) {
      dragStateRef.current.lastY = rigRef.current.rotation.y;
      dragStateRef.current.initialized = true;
    }


    const handlePointerDown = (e) => {
      // console.log('[DRAG] pointerdown moveActiveRef.current:', moveActiveRef.current);
      // Solo permitir drag si NO hay animación
      if (moveActiveRef.current) return;
      dragStateRef.current.isDragging = true;
      dragStateRef.current.startX = e.clientX || e.touches?.[0]?.clientX || 0;
      if (rigRef.current) {
        dragStateRef.current.lastY = rigRef.current.rotation.y;
      }
    };

    const handlePointerMove = (e) => {
      // console.log('[DRAG] pointermove moveActiveRef.current:', moveActiveRef.current);
      // Solo permitir drag si isDragging (que solo se activa si no hay animación)
      if (!dragStateRef.current.isDragging) return;
      // Si la animación arrancó mientras estaba el drag, cancelar drag
      if (moveActiveRef.current) {
        dragStateRef.current.isDragging = false;
        return;
      }
      const currentX = e.clientX || e.touches?.[0]?.clientX || 0;
      const deltaX = currentX - dragStateRef.current.startX;
      let rotationDelta = deltaX * 0.01;
      let newY = dragStateRef.current.lastY + rotationDelta;
      newY = Math.atan2(Math.sin(newY), Math.cos(newY));
      if (rigRef.current) {
        rigRef.current.rotation.y = newY;
        // console.log('[DRAG] Rotación rig (radianes):', newY, '| (grados):', (newY * 180 / Math.PI).toFixed(2));
      }
    };

    const handlePointerUp = () => {
      // console.log('[DRAG] pointerup moveActiveRef.current:', moveActiveRef.current);
      // Solo permitir finalizar drag si isDragging
      if (!dragStateRef.current.isDragging) return;
      // Si la animación arrancó mientras estaba el drag, cancelar drag
      if (moveActiveRef.current) {
        dragStateRef.current.isDragging = false;
        return;
      }
      dragStateRef.current.isDragging = false;
      if (rigRef.current) {
          // Normalizar rotación después del drag
          rigRef.current.rotation.y = Math.atan2(Math.sin(rigRef.current.rotation.y), Math.cos(rigRef.current.rotation.y));
          dragStateRef.current.lastY = rigRef.current.rotation.y;
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('touchstart', handlePointerDown);
    window.addEventListener('touchmove', handlePointerMove);
    window.addEventListener('touchend', handlePointerUp);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('touchstart', handlePointerDown);
      window.removeEventListener('touchmove', handlePointerMove);
      window.removeEventListener('touchend', handlePointerUp);
    };
  }, []);


useEffect(() => {
  if (readySentRef.current) return;
  if (!actions || Object.keys(actions).length === 0) return;

  readySentRef.current = true;
  onReady?.(); // ✅ avisa al padre
}, [actions, onReady]);

const audioRef = useRef(null);
const unlockAudio = async () => {
  // play() ya hace resume() internamente, así que “unlock” puede ser un wrapper
  // que llames desde un tap/click del usuario.
  try {
    await audioRef.current?.play?.();
    // si no querés que empiece a sonar al unlock, frenalo inmediatamente con fadeOut corto:
    // await audioRef.current?.stop?.();
    return true;
  } catch {
    return false;
  }
};

const playAudio = () => {
  audioRef.current?.play?.();
};

const stopAudio = () => {
  audioRef.current?.stop?.();
};


useEffect(() => {
  if (!actions || Object.keys(actions).length === 0) return;
  if (!rigRef.current) return;
  if (didAutoStartRef.current) return;

  didAutoStartRef.current = true;

  // estado inicial (opcional, por si querés asegurar el start)
  const { position, rotationY } = TRANSFORMS.start;

  rigRef.current.position.copy(position);
  rigRef.current.rotation.y = rotationY;
}, [actions]);



useEffect(() => {
  if (!actions) return;
  const ok = Object.keys(actions).length > 0;
  readyRef.current = ok;
}, [actions]);

useEffect(() => {
  return () => {
    if (afterTakeoffTimeoutRef.current) {
      clearTimeout(afterTakeoffTimeoutRef.current);
      afterTakeoffTimeoutRef.current = null;
    }
  };
}, []);

// useEffect(() => {
//   if (!actions) return;

//   const introMain =
//     actions["Helix_spinUp"] ||
//     INTRO_CLIPS.map((n) => actions[n]).find(Boolean);

//   if (!introMain) return;

//   const mixer = introMain.getMixer();

//   const onFinished = (e) => {
//     // asegurate que sea el clip de takeoff el que terminó
//     if (e.action !== introMain) return;

//     playFly();


//     if (lastIntroWasTakeoffRef.current) {
//       // Evita duplicados
//       if (afterTakeoffTimeoutRef.current) clearTimeout(afterTakeoffTimeoutRef.current);

//       afterTakeoffTimeoutRef.current = setTimeout(() => {
//         afterTakeoffTimeoutRef.current = null;

//         // mover a flyAway (podés ajustar duración)

//         moveTo(
//           { x: TRANSFORMS.flyAway.position.x, y: TRANSFORMS.flyAway.position.y, z: TRANSFORMS.flyAway.position.z },
//           {
//             duration: 3.5,
//             delay: 0,
//             fromRotY: rigRef.current.rotation.y, // no usado
//             toRotY: TRANSFORMS.flyAway.rotationY, // no usado
//             // captureFromOnStart: true, // no usado
//             // phase: null, // no usado
//             // phaseOnComplete: null, // no usado
//             onComplete: () => {
//               if (flyBackTimeoutRef.current) clearTimeout(flyBackTimeoutRef.current);

//               flyBackTimeoutRef.current = setTimeout(() => {
//                 flyBackTimeoutRef.current = null;

//                 // Cambiar a estado partnership
//                 advanceFlightState();
//                 // Esperar 10 segundos antes de volver
//                 setTimeout(() => {
//                   // Cambiar primero el estado para ocultar el botón
//                   advanceFlightState();
//                   // para que intro no auto-dispare fly de nuevo si lo estás bloqueando
//                   suppressIntroAutoFlyRef.current = true;
//                 }, 10000);
//               }, 1000);
//             },
//           }
//         );

//       }, 2000);

//       return;
//     }

//   };

//   mixer.addEventListener("finished", onFinished);
//   return () => mixer.removeEventListener("finished", onFinished);
// }, [actions]);

useEffect(() => {
  if (!actions) return;

  const landMain =
    actions["Helix_Landing"] ||
    END_CLIPS.map((n) => actions[n]).find(Boolean);

  if (!landMain) return;

  const mixer = landMain.getMixer();

  const onFinished = (e) => {
    if (e.action !== landMain) return;
    stopAudio(); 
    advanceFlightState();
  };

  mixer.addEventListener("finished", onFinished);
  return () => mixer.removeEventListener("finished", onFinished);
}, [actions]);



const stopAll = () => {
  if (!actions) return;
  Object.values(actions).forEach((a) => a?.stop());
};

const playGroup = (names, { loop = THREE.LoopRepeat, clampWhenFinished = false, reset = true, onComplete = null } = {}) => {
  if (!actions) return;
  let finishedCount = 0;
  const total = names.length;
  const listeners = [];

  names.forEach((name) => {
    const a = actions[name];
    if (!a) {
      console.warn('[Helicopter] Acción no encontrada:', name);
      return;
    }
    a.enabled = true;
    a.setEffectiveWeight(1);
    a.setEffectiveTimeScale(1);
    a.setLoop(loop, loop === THREE.LoopRepeat ? Infinity : 1);
    a.clampWhenFinished = clampWhenFinished;
    if (reset) a.reset();

    // Si hay callback y la animación no es LoopRepeat infinito, agregamos listener
    if (onComplete && loop !== THREE.LoopRepeat) {
      const onFinish = (e) => {
        if (e.action === a) {
          finishedCount++;
          if (finishedCount === total) {
            onComplete();
            // Remover listeners
            listeners.forEach(({ action, fn }) => action.getMixer().removeEventListener('finished', fn));
          }
        }
      };
      a.getMixer().addEventListener('finished', onFinish);
      listeners.push({ action: a, fn: onFinish });
    }

    a.play();
  });
};

const playIntro = () => {
  if (introStartedRef.current) return;
  introStartedRef.current = true;
  lastIntroWasTakeoffRef.current = false; // que onda esto??

  playAudio();
  const rig = rigRef.current;
  if (!rig) return;
  // Normalizar rotación antes de animar
  rig.rotation.y = Math.atan2(Math.sin(rig.rotation.y), Math.cos(rig.rotation.y));
  const to = TRANSFORMS.loop;
  stopAll();
  playGroup([...LOOP_CLIPS], {
    loop: THREE.LoopRepeat,
    reset: true,
  });

  moveTo(
    { x: to.position.x, y: to.position.y, z: to.position.z },
    {
      duration: 5,
      delay: 0.0,
      fromRotY: rigRef.current.rotation.y,
      toRotY: to.rotationY,
      onComplete: () => {
        advanceFlightState();
      }
    },
  );
};

const playTakeoff = () => {

  playAudio();
  // lastIntroWasTakeoffRef.current = true;

  // if (afterTakeoffTimeoutRef.current) {
  //   clearTimeout(afterTakeoffTimeoutRef.current);
  //   afterTakeoffTimeoutRef.current = null;
  // }

  const rig = rigRef.current;
  if (!rig) return;
  // Normalizar rotación antes de animar
  rig.rotation.y = Math.atan2(Math.sin(rig.rotation.y), Math.cos(rig.rotation.y));
  const to_1 = TRANSFORMS.loop;
  const to_2 = TRANSFORMS.flyAway;
  stopAll();
  playGroup([...INTRO_CLIPS], {
    loop: THREE.LoopOnce,
    clampWhenFinished: true,
    reset: true,
    onComplete: () => {
      playFly();
    },
  });

  introStartedRef.current = false;
  console.log("[INITIAL ROT] ", rigRef.current.rotation.y);
  console.log("[TO_1 ROT] ", to_1.rotationY);
  moveTo(
    { x: to_1.position.x, y: to_1.position.y, z: to_1.position.z },
    {
      duration: 3,
      delay: 0.0,
      fromRotY: rigRef.current.rotation.y,
      toRotY: to_1.rotationY,
      onComplete: () => {
        setTimeout(() => {
          // Normalizar antes de la segunda animación
          rigRef.current.rotation.y = Math.atan2(Math.sin(rigRef.current.rotation.y), Math.cos(rigRef.current.rotation.y));
          moveTo(
            { x: to_2.position.x, y: to_2.position.y, z: to_2.position.z },
            {
              duration: 3,
              delay: 0.0,
              fromRotY: rigRef.current.rotation.y,
              toRotY: to_2.rotationY,
              onComplete: () => {
                console.log("Takeoff parte 2 completa, vuelo terminado");
                // Cambia a estado partnership
                advanceFlightState();
                setTimeout(() => {
                  // Reinicia y cambia a estado flyApproaching
                  advanceFlightState();
                  // Resetea la posicion y rotación a los valores iniciales
                  rigRef.current.position.copy(TRANSFORMS.start.position);
                  rigRef.current.rotation.y = TRANSFORMS.start.rotationY;
                }, 5000);
              }
            }
          );
        }, 10000);
      }
    }
  );
};

const playFly = () => {
  console.log('Helicopter: playFly()');
  const rig = rigRef.current;
  if (!rig) return;

  stopAll(); 
  playGroup([...LOOP_CLIPS], {
    loop: THREE.LoopRepeat,
    reset: true,
  });
};

const playLand = () => {
  const rig = rigRef.current;
  if (!rig) return;
  // Normalizar rotación antes de animar
  rig.rotation.y = Math.atan2(Math.sin(rig.rotation.y), Math.cos(rig.rotation.y));
  const to = TRANSFORMS.land;

  stopAll();
  playGroup(END_CLIPS, {
    loop: THREE.LoopOnce,
    clampWhenFinished: true,
    reset: true,
  });

  moveTo(
    { x: to.position.x, y: to.position.y, z: to.position.z },
    {
      duration: 3,
      delay: 0.0,
      fromRotY: rigRef.current.rotation.y,
      toRotY: to.rotationY,
      captureFromOnStart: false,
      onComplete: () => {},
    }
  );
};

useImperativeHandle(
  ref,
  () => ({
    isReady: () => readyRef.current,
    playIntro,          // 1) playIntro (si querés mantenerlo)
    playTakeoff: playTakeoff, // 2) takeoff = intro
    // playFly: playFly,      // 3) fly = loop
    playLand: playLand,      // 4) land = end
    unlockAudio,
    playAudio,
    stopAudio,
    moveTo,
  }),
  [actions]
); // TODO: ver si lo puedo exportar de otra manera

useEffect(() => {
  if (!actions) return;
  Object.values(actions).forEach((a) => {
    if (!a) return;
    a.paused = !playing;
  });
}, [actions, playing]);

const scene = useMemo(() => {
  const clonedScene = originalScene.clone();
  // const lightPosition = new THREE.Vector3(lightPos); // se usa?
  
  clonedScene.traverse((child) => {
    if (child.isMesh) {

      child.material = child.material.clone();
      
      const originalTexture = child.material.map;
        if (child.material.name === "Atlas_BaseColor") {
          child.material.metalness = 0.95;
          child.material.roughness = 0.4;
          child.material.color = new THREE.Color(0xb5b5b5);
          child.material.clearcoatRoughness = 1.0;

          // clave para que se actualice el shader
          child.material.needsUpdate = true;
        }

        if (castShadow) child.castShadow = true;
      
      if (castShadow) {
        child.castShadow = true;
      }
    }
  });
  
  return clonedScene;
}, []);

  // ANIMACIÓN
  useFrame((state, delta) => {
    updateMovement();

  // =========================
  // 4) Shadow opacity (igual que antes)
  // =========================
  setShadowOpacity((current) => {
    const target = targetShadowOpacityRef.current;
    const next = current + (target - current) * delta;
    return Math.abs(next - target) < 0.001 ? target : next;
  });
});

  return (
    <>
      <AudioLooper
        ref={audioRef}
        url={EXTERIOR_RESOURCES.SonidoHelicoptero.url}
        fadeIn={2.0}
        fadeOut={1.5}
        volume={1}
        loopStart={0.0}
        loopEndTrim={0.0}
      />
      <group ref={rigRef}>
        <primitive
          ref={helicopterRef}
          object={scene}
          scale={0.05}
          position={[0, 0, 0]}
          // rotation-y={-Math.PI/2}
          {...primitiveProps}
        />
        {castShadow && (
          <ContactShadows scale={5} blur={3} opacity={shadowOpacity} far={15} />
        )}
      </group>
    </>
  );
})

export default Helicopter;